import React, { createContext, useContext, useState, useCallback } from "react";
import { CheckCircle2, AlertCircle, AlertTriangle, Info, X } from "lucide-react";

export type ToastType = "success" | "error" | "info" | "warning";

export interface ToastItem {
  id: string;
  type: ToastType;
  title?: string;
  message: string;
  duration?: number;
}

export interface ToastMethods {
  success: (message: string, title?: string) => void;
  error: (message: string, title?: string) => void;
  warning: (message: string, title?: string) => void;
  info: (message: string, title?: string) => void;
}

export interface ToastContextValue extends ToastMethods {
  showToast: (message: string, options?: { type?: ToastType; title?: string; duration?: number }) => void;
  dismissToast: (id: string) => void;
  toast: ToastMethods;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

export function ToastProvider({ children }: { children: React.ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);

  const dismissToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  const showToast = useCallback(
    (message: string, options?: { type?: ToastType; title?: string; duration?: number }) => {
      const id = crypto.randomUUID();
      const type = options?.type || "info";
      const duration = options?.duration ?? 4500;
      const title = options?.title;

      setToasts((prev) => [...prev, { id, type, title, message, duration }]);

      if (duration > 0) {
        setTimeout(() => {
          dismissToast(id);
        }, duration);
      }
    },
    [dismissToast]
  );

  const success = useCallback(
    (message: string, title?: string) => showToast(message, { type: "success", title }),
    [showToast]
  );
  const error = useCallback(
    (message: string, title?: string) => showToast(message, { type: "error", title }),
    [showToast]
  );
  const warning = useCallback(
    (message: string, title?: string) => showToast(message, { type: "warning", title }),
    [showToast]
  );
  const info = useCallback(
    (message: string, title?: string) => showToast(message, { type: "info", title }),
    [showToast]
  );

  return (
    <ToastContext.Provider
      value={{
        showToast,
        success,
        error,
        warning,
        info,
        dismissToast,
        toast: { success, error, warning, info },
      }}
    >
      {children}

      {/* Floating Bottom-Right Toast Stack */}
      <aside
        aria-live="polite"
        aria-atomic="true"
        className="fixed bottom-4 right-4 z-100 flex flex-col gap-2 max-w-sm w-full pointer-events-none px-3 sm:px-0"
      >
        {toasts.map((toast) => {
          let Icon = Info;
          let containerStyles = "bg-stone-900 border-stone-700 text-stone-100";
          let iconStyles = "text-stone-400";

          if (toast.type === "success") {
            containerStyles = "bg-white dark:bg-stone-900 border-emerald-500/40 text-stone-900 dark:text-stone-100 shadow-lg";
            iconStyles = "text-emerald-600 dark:text-emerald-400";
            Icon = CheckCircle2;
          } else if (toast.type === "error") {
            containerStyles = "bg-white dark:bg-stone-900 border-rose-500/40 text-stone-900 dark:text-stone-100 shadow-lg";
            iconStyles = "text-rose-600 dark:text-rose-400";
            Icon = AlertCircle;
          } else if (toast.type === "warning") {
            containerStyles = "bg-white dark:bg-stone-900 border-amber-500/40 text-stone-900 dark:text-stone-100 shadow-lg";
            iconStyles = "text-amber-600 dark:text-amber-400";
            Icon = AlertTriangle;
          } else {
            containerStyles = "bg-white dark:bg-stone-900 border-stone-300 dark:border-stone-700 text-stone-900 dark:text-stone-100 shadow-lg";
            iconStyles = "text-stone-500 dark:text-stone-400";
            Icon = Info;
          }

          return (
            <div
              key={toast.id}
              role="alert"
              className={`pointer-events-auto flex items-start gap-3 p-3.5 rounded-xl border backdrop-blur-md transition-all duration-200 animate-in fade-in slide-in-from-bottom-3 ${containerStyles}`}
            >
              <Icon className={`w-4 h-4 shrink-0 mt-0.5 ${iconStyles}`} />
              <div className="flex-1 min-w-0">
                {toast.title && (
                  <h4 className="text-xs font-semibold leading-tight mb-0.5">
                    {toast.title}
                  </h4>
                )}
                <p className="text-xs text-stone-600 dark:text-stone-300 leading-snug whitespace-pre-wrap break-words">
                  {toast.message}
                </p>
              </div>
              <button
                type="button"
                onClick={() => dismissToast(toast.id)}
                className="text-stone-400 hover:text-stone-600 dark:hover:text-stone-200 p-0.5 rounded transition shrink-0"
                aria-label="Dismiss toast"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          );
        })}
      </aside>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error("useToast must be used within a ToastProvider");
  }
  return context;
}
